import { useMemo } from "react";
import { FieldValues, useController, UseControllerProps } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { CountryIso2, defaultCountries, FlagImage, parseCountry, usePhoneInput } from "react-international-phone";
import { TCountryCode, valueExists } from "@monorepo/helpers";
import { useGetSettingsQuery } from "@monorepo/services/api/misc";
import theme from "@monorepo/styles/theme";
import { InputAdornment, MenuItem, Select, TextField, Typography } from "@mui/material";

import "./css/index.css";

export type TSetSelectedPhoneCountry = ({ countryCode }: { countryCode: TCountryCode }) => void;

type TPhoneInputProps<TFieldValues extends FieldValues> = UseControllerProps<TFieldValues> & {
  required?: boolean;
  setSelectedPhoneCountry: TSetSelectedPhoneCountry;
  autoFocus?: boolean;
  disabled?: boolean;
};

export const PhoneInput = <TFieldValues extends FieldValues>({
  name,
  control,
  setSelectedPhoneCountry,
  autoFocus = false,
  disabled = false,
}: TPhoneInputProps<TFieldValues>) => {
  const { data: settings } = useGetSettingsQuery();
  const { t } = useTranslation();
  const {
    field,
    fieldState: { error },
  } = useController({
    name,
    control,
  });
  const filteredCountries = useMemo(() => {
    if (!valueExists(settings) || !valueExists(settings.countries)) {
      return [];
    }
    const backendCountryCodes = settings.countries.map((country) => country.code.toLowerCase());
    return defaultCountries
      .filter((defaultCountry) => {
        const { iso2 } = parseCountry(defaultCountry);
        if (!valueExists(settings) || !valueExists(settings.countries)) {
          return [];
        }
        return backendCountryCodes.includes(iso2);
      })
      .sort((a, b) => {
        const { iso2: iso2A } = parseCountry(a);
        const { iso2: iso2B } = parseCountry(b);
        return backendCountryCodes.indexOf(iso2A) - backendCountryCodes.indexOf(iso2B);
      });
  }, [settings]);

  const { handlePhoneValueChange, inputRef, country, setCountry, inputValue } = usePhoneInput({
    defaultCountry: "lt",
    forceDialCode: true,
    countries: filteredCountries.length > 0 ? filteredCountries : defaultCountries,
    value: field.value,
    onChange: (data) => {
      setSelectedPhoneCountry({ countryCode: data.country.iso2.toUpperCase() as TCountryCode });
      if (`+${data.country.dialCode}` === data.phone) {
        return;
      }
      field.onChange(data.phone.replaceAll(/[ ()+]/g, ""));
    },
  });
  return (
    <TextField
      variant="standard"
      autoFocus={autoFocus}
      color="primary"
      label={t("register.phone_number")}
      placeholder={t("register.phone_number")}
      error={valueExists(error)}
      helperText={valueExists(error) && typeof error.message !== "undefined" ? <>{t(error.message)}</> : null}
      type="tel"
      inputRef={inputRef}
      disabled={disabled}
      InputProps={{
        style: {
          marginTop: "20px",
          paddingBottom: "10px",
        },
        sx: {
          "&::after": {
            top: "30px",
          },
          "& .MuiInputBase-input.MuiInput-input ": {
            padding: "10px",
            borderBottom: "0px!important",
          },
        },
        endAdornment: (
          <InputAdornment
            position="start"
            style={{
              marginRight: "2px",
            }}
          >
            <Select
              disabled={disabled}
              MenuProps={{
                anchorOrigin: { vertical: "top", horizontal: "right" },
                anchorEl: inputRef.current,
                MenuListProps: {
                  sx: {
                    "li:hover": {
                      color: "white",
                    },
                  },
                },
                PaperProps: {
                  sx: {
                    width: "100%",
                    "& .MuiMenuItem-root.Mui-selected  > p": {
                      color: `${theme.palette.text.primary} !important`,
                    },
                    "& .MuiMenuItem-root:hover  > p": {
                      color: `${theme.palette.text.primary} !important`,
                    },
                    "& .MuiMenuItem-root.Mui-selected:hover  > p": {
                      color: `${theme.palette.text.primary} !important`,
                    },

                    "::-webkit-scrollbar": {
                      width: 5,
                    },
                    "::-webkit-scrollbar-corner": {
                      background: "transparent",
                    },
                    "::-webkit-scrollbar-thumb,  *::-webkit-scrollbar-thumb": {
                      borderRadius: 10,
                      backgroundColor: `${theme.palette.grey[400]}`,
                    },
                    "::-webkit-scrollbar-track,  *::-webkit-scrollbar-track": {
                      borderRadius: 10,
                      backgroundColor: `${theme.palette.grey[300]}`,
                    },
                    "::-webkit-scrollbar-thumb:focus,  *::-webkit-scrollbar-thumb:focus": {
                      backgroundColor: `${theme.palette.grey[600]}`,
                    },
                    "::-webkit-scrollbar-thumb:active,  *::-webkit-scrollbar-thumb:active": {
                      backgroundColor: `${theme.palette.grey[600]}`,
                    },
                    "::-webkit-scrollbar-thumb:hover,  *::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: `${theme.palette.grey[500]}`,
                    },
                  },
                },

                style: {
                  height: "300px",
                  width: "140px",
                  top: "30px",
                },
              }}
              sx={{
                width: "max-content",
                // Remove default outline (display only on focus)
                fieldset: {
                  display: "none",
                },
                // eslint-disable-next-line quotes
                '&.Mui-focused:has(div[aria-expanded="false"])': {
                  fieldset: {
                    display: "block",
                  },
                },
                // Update default spacing
                ".MuiSelect-select": {
                  padding: "8px !important",
                  paddingRight: "24px !important",
                },
                svg: {
                  right: 0,
                },
              }}
              value={country.iso2}
              onChange={(e) => setCountry(e.target.value as CountryIso2)}
              renderValue={(val) => <FlagImage iso2={val} style={{ display: "flex" }} />}
            >
              {filteredCountries.length > 0
                ? filteredCountries.map((defaultCountry) => {
                    const parsedCountry = parseCountry(defaultCountry);
                    return (
                      <MenuItem key={parsedCountry.iso2} value={parsedCountry.iso2}>
                        <FlagImage iso2={parsedCountry.iso2} style={{ marginRight: "8px" }} />
                        <Typography color="gray">+{parsedCountry.dialCode}</Typography>
                      </MenuItem>
                    );
                  })
                : null}
            </Select>
          </InputAdornment>
        ),
      }}
      onBlur={(e) => {
        if (e.target.value[e.target.value.length - 1] !== " ") field.onBlur();
      }}
      value={inputValue}
      onChange={handlePhoneValueChange}
      sx={{
        width: "100%",
        color: theme.palette.text.primary,
      }}
    />
  );
};
